import React from 'react';

import DatePicker from '../../../components/common/DatePicker';
import { dateToString, stringToDate } from '../../helpers/DateFormatter';
import FormGroupNumberRange from '../formElements/FormGroupNumberRange';

import { ColumnFilters, FilterChangeCallback, FilterComponentProps } from './types';

type Props = Readonly<{
    selected: FilterComponentProps;
    filters: ColumnFilters;
    onChange: FilterChangeCallback;
}>;

export default function FilterInput({ selected, filters, onChange }: Props) {
    if (!selected) {
        return null;
    }

    const numbersChange = (numbers: [number, number]) =>
        onChange(selected.column, numbers[0] || numbers[1] ? ['numbers', ...numbers] : undefined);

    const datesChange = (dates: [Date, Date]) =>
        onChange(
            selected.column,
            dates[0] || dates[1] ? ['dates', dateToString(dates[0]), dateToString(dates[1])] : undefined
        );

    const valueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, search = false) => {
        const value = e.target.value;
        onChange(selected.column, value !== '' ? [search ? 'search' : 'value', value] : undefined);
    };

    switch (selected.type) {
        case 'dateRange':
            return (
                <div className="d-inline-block ml-1">
                    <DatePicker
                        isClearable
                        placeholderText="choose a date range"
                        selected={stringToDate(filters[selected.column]?.[1] as string)}
                        onChange={datesChange}
                        startDate={stringToDate(filters[selected.column]?.[1] as string)}
                        endDate={stringToDate(filters[selected.column]?.[2] as string)}
                        selectsRange
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                    />
                </div>
            );

        case 'numberRange':
            return (
                <div className="d-inline-flex ml-1" style={{ maxWidth: '14em' }}>
                    <FormGroupNumberRange
                        minValue={filters[selected.column]?.[1] as number}
                        maxValue={filters[selected.column]?.[2] as number}
                        onChange={numbersChange}
                    />
                </div>
            );

        case 'select':
            return (
                <div className="form-group d-inline-block ml-1">
                    <select
                        className="form-control"
                        value={(filters[selected.column]?.[1] as string) ?? ''}
                        onChange={valueChange}
                    >
                        <option value="" className="text-muted">
                            {filters[selected.column] ? 'clear the filter' : 'select an option'}
                        </option>
                        {Object.entries(selected.options).map(([key, value]) => (
                            <option key={key} value={key}>
                                {value}
                            </option>
                        ))}
                    </select>
                </div>
            );

        case 'text':
        case 'search':
        default:
            return (
                <div className="form-group d-inline-block ml-1">
                    <input
                        type="search"
                        className="form-control"
                        placeholder="type a value"
                        value={(filters[selected.column]?.[1] as string) ?? ''}
                        onChange={(e) => valueChange(e, selected.type === 'search')}
                    />
                </div>
            );
    }
}
